import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import CalendarView from './CalendarView';
import { ProjectedTaskDetail, TemplateJobProjectionResponse } from '../../../../interfaces/adminUser/adminRecurringTaskStats/AdminRecurringTaskJobStats';
import { getMonthlyProjections, getRetroactiveMissedTasks } from '../../../../services/adminStatsServices/AdminRecurringTaskJobStatsService';
import { useAuth } from '../../../../firebase/AuthProvider';
import DetailList, { MissedTasksView } from './DetailList';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faCalendarAlt, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';

// Mock Orgs for now
interface OrganizationOption { id: number; name: string; }
const MOCK_ORGS: OrganizationOption[] = [ { id: 1, name: "SAMPLE SCA AGENCY" }, { id: 100, name: "Easter Seals" } ];

export const AdminTemplateDashboard: React.FC = () => {
    const navigate = useNavigate();
    const { currentUser } = useAuth();

    // --- State ---
    const [selectedOrg, setSelectedOrg] = useState<number>(MOCK_ORGS[0].id);
    const [year, setYear] = useState<number>(new Date().getFullYear());
    const [month, setMonth] = useState<number>(new Date().getMonth() + 1);
    
    const [projectionData, setProjectionData] = useState<TemplateJobProjectionResponse | null>(null);
    const [missedTasks, setMissedTasks] = useState<ProjectedTaskDetail[]>([]);
    const [viewMode, setViewMode] = useState<'calendar' | 'missed'>('calendar');
    
    const [loading, setLoading] = useState<boolean>(false);
    
    // Panel State
    const [selectedDayDetails, setSelectedDayDetails] = useState<ProjectedTaskDetail[] | null>(null);
    const [selectedDateLabel, setSelectedDateLabel] = useState<string>("");
    const [isPanelOpen, setIsPanelOpen] = useState(false);

    useEffect(() => {
const fetchData = async () => {
        if (!currentUser) return;
        setLoading(true);
        const token = await currentUser.getIdToken();

        try {
            if (viewMode === 'calendar') {
                const data = await getMonthlyProjections(token, selectedOrg, year, month);
                setProjectionData(data);
            } else {
                const missed = await getRetroactiveMissedTasks(token, selectedOrg);
                setMissedTasks(missed || []);
            }
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

        fetchData();
    }, [selectedOrg, year, month, viewMode, currentUser]);



    // FIX: Receive tasks directly from the CalendarView
    const handleDayClick = (tasks: ProjectedTaskDetail[], dateLabel: string) => {
        if (tasks && tasks.length > 0) {
            setSelectedDayDetails(tasks);
            setSelectedDateLabel(dateLabel);
            setIsPanelOpen(true);
        }
    };

    return (
        <div className="p-6 bg-white rounded-lg mx-auto">
            {/* Header & Back */}
            <div className="mb-6">
                <button 
                    onClick={() => navigate('/dashboard/superadmin')} 
                    className="flex items-center text-[#7B5CD6] hover:text-[#5e45a3] font-medium text-sm mb-4 transition-colors"
                >
                    <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
                    Back to Dashboard
                </button>
                <div className="flex flex-col md:flex-row justify-between md:items-center gap-4">
                    <h1 className="text-2xl font-bold text-gray-800">Recurring Task Projections</h1>
                    
                    {/* Controls */}
                    <div className="flex gap-2">
                        <select 
                            className="border border-gray-300 rounded-md p-2 text-sm focus:ring-1 focus:ring-[#7B5CD6] focus:border-[#7B5CD6]"
                            value={selectedOrg}
                            onChange={(e) => setSelectedOrg(Number(e.target.value))}
                        >
                            {MOCK_ORGS.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                        </select>
                        <select 
                            className="border border-gray-300 rounded-md p-2 text-sm focus:ring-1 focus:ring-[#7B5CD6] focus:border-[#7B5CD6]"
                            value={month}
                            onChange={(e) => setMonth(Number(e.target.value))}
                        >
                            {Array.from({length: 12}, (_, i) => i + 1).map(m => (
                                <option key={m} value={m}>{new Date(0, m-1).toLocaleString('default', { month: 'long' })}</option>
                            ))}
                        </select>
                        <select 
                            className="border border-gray-300 rounded-md p-2 text-sm focus:ring-1 focus:ring-[#7B5CD6] focus:border-[#7B5CD6]"
                            value={year}
                            onChange={(e) => setYear(Number(e.target.value))}
                        >
                            <option value={2025}>2025</option>
                            <option value={2026}>2026</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* View Toggles */}
            <div className="flex border-b border-gray-200 mb-6">
                <button
                    onClick={() => setViewMode('calendar')}
                    className={`px-6 py-3 font-medium text-sm transition-colors duration-200 relative ${
                        viewMode === 'calendar' ? 'text-[#7B5CD6]' : 'text-gray-500 hover:text-gray-700'
                    }`}
                >
                    <FontAwesomeIcon icon={faCalendarAlt} className="mr-2" />
                    Calendar Projection
                    {viewMode === 'calendar' && <span className="absolute bottom-0 left-0 w-full h-0.5 bg-[#7B5CD6]"></span>}
                </button>
                <button
                    onClick={() => setViewMode('missed')}
                    className={`px-6 py-3 font-medium text-sm transition-colors duration-200 relative ${
                        viewMode === 'missed' ? 'text-[#7B5CD6]' : 'text-gray-500 hover:text-gray-700'
                    }`}
                >
                    <FontAwesomeIcon icon={faExclamationTriangle} className="mr-2" />
                    Missed Tasks Check
                    {viewMode === 'missed' && <span className="absolute bottom-0 left-0 w-full h-0.5 bg-[#7B5CD6]"></span>}
                </button>
            </div>

            {/* Content */}
            {loading ? (
                <div className="p-12 text-center text-gray-500 bg-white rounded-lg border border-gray-100 shadow-sm">
                    <div className="animate-pulse">Loading Projections...</div>
                </div>
            ) : (
                <>
                    {viewMode === 'calendar' && projectionData && (
                        <CalendarView 
                            year={year} 
                            month={month} 
                            data={projectionData} 
                            onDayClick={handleDayClick} 
                        />
                    )}

                    {viewMode === 'missed' && (
                        <MissedTasksView tasks={missedTasks} />
                    )}
                </>
            )}

            {/* Side Panel */}
            {isPanelOpen && (
                <div className="fixed inset-0 bg-gray-900 bg-opacity-30 z-50 flex justify-end backdrop-blur-sm transition-opacity" onClick={() => setIsPanelOpen(false)}>
                    <div className="bg-white w-full max-w-lg h-full shadow-2xl overflow-y-auto p-6 animate-slide-in" onClick={e => e.stopPropagation()}>
                        <div className="flex justify-between items-center mb-6 border-b border-gray-100 pb-4">
                            <div>
                                <h2 className="text-xl font-bold text-gray-800">Projected Details</h2>
                                {selectedDateLabel && <p className="text-xs text-gray-500">{selectedDateLabel}</p>}
                            </div>
                            <button onClick={() => setIsPanelOpen(false)} className="text-gray-400 hover:text-gray-600 text-xl font-bold">
                                ✕
                            </button>
                        </div>
                        <DetailList tasks={selectedDayDetails || []} />
                    </div>
                </div>
            )}
        </div>
    );
};